import { NumberFormatInput, NumberFormatInputProps, NumberInputType } from "@centricsoftware/design-system";
import { VALUE_TYPE } from "c8-platform";
import {
	DECREMENT_RES,
	INCREMENT_RES,
	INVALID_NUMBER_RES,
	NEGATIVE_NOT_ALLOWED_RES,
	OUT_OF_RANGE_RES
} from "csi/components/constants";
import {
	countDecimalPlaces,
	DEFAULT_DECIMALS,
	getCurrencySymbol,
	getNumberDecimal,
	getNumberGroup,
	isDecimalAllZero,
	isPercentFormat,
} from "csi/utility/numberUtility";
import React, { FunctionComponent, useMemo } from "react";

export type NumberValueType = typeof VALUE_TYPE.DOUBLE | typeof VALUE_TYPE.INTEGER;

export interface NumberComponentProps extends Omit<NumberFormatInputProps, "type"> {
	/**
	 * format of value
	 */
	format: string;

	/**
	 * type of value VALUE_TYPE.DOUBLE or VALUE_TYPE.INTEGER
	 */
	valueType: NumberValueType;
}

const NumberComponent: FunctionComponent<NumberComponentProps> = (
	{
		format,
		onValidation,
		valueType = VALUE_TYPE.INTEGER,
		...otherProps
	},
) => {
	const decimalSeparator = getNumberDecimal();
	const groupSeparator = getNumberGroup();
	const prefix =  useMemo(() => getCurrencySymbol(format), [format]);
	const suffix = useMemo(() => isPercentFormat(format) ? "%" : "", [format]);
	const decimalPlaces =  useMemo(() => format ?
		countDecimalPlaces({ format, decimalSeparator, prefix, suffix })
		: DEFAULT_DECIMALS,
	[ format, decimalSeparator, prefix, suffix ]);
	const fixedDecimalPlaces = useMemo(() =>
		isDecimalAllZero({ format, decimalSeparator, prefix, suffix }),
	[ format, decimalSeparator, prefix, suffix ]);
	const type = useMemo(() => valueType === VALUE_TYPE.INTEGER ? NumberInputType.Integer : NumberInputType.Decimal, [valueType]);

	return (
		<NumberFormatInput
			{...otherProps}
			decimalPlaces={decimalPlaces}
			decimalSeparator={decimalSeparator}
			decrementButtonAriaLabelRes={DECREMENT_RES}
			defaultInvalidNumberErrorMessageRes={INVALID_NUMBER_RES}
			defaultNegativeNotAllowedErrorMessageRes={NEGATIVE_NOT_ALLOWED_RES}
			defaultOutOfRangeErrorMessageRes={OUT_OF_RANGE_RES}
			fixedDecimalPlaces={fixedDecimalPlaces}
			incrementButtonAriaLabelRes={INCREMENT_RES}
			onValidation={onValidation}
			prefix={prefix}
			suffix={suffix}
			thousandSeparator={groupSeparator}
			type={type}
		/>
	);
};

export default NumberComponent;
